import React from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/Breadcrumb';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { MapPin, Info, Clock, Calendar, ShieldAlert, FileText } from 'lucide-react';
import { procedures } from '../data/procedures';
export function ProcedureDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const procedure = procedures.find(p => p.slug === slug);
  if (!procedure) {
    return <Navigate to="/not-found" replace data-id="element-1283" />;
  }
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://affordablemedicaltourism.com/'
    }, {
      '@type': 'ListItem',
      position: 2,
      name: 'Procedures',
      item: 'https://affordablemedicaltourism.com/procedures'
    }, {
      '@type': 'ListItem',
      position: 3,
      name: procedure.name,
      item: `https://affordablemedicaltourism.com/procedures/${procedure.slug}`
    }]
  };
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1284">
      <Helmet data-id="element-1285">
        <title data-id="element-1286">{procedure.name} Abroad | Affordable Medical Tourism</title>
        <meta name="description" content={`Learn about affordable, high-quality ${procedure.name.toLowerCase()} abroad. Discover destinations, illustrative pricing, and our rigorous vetting process.`} data-id="element-1287" />
        <link rel="canonical" href={`https://affordablemedicaltourism.com/procedures/${procedure.slug}`} data-id="element-1288" />
        <script type="application/ld+json" data-id="element-1289">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <AppHeader data-id="element-1290" />

      <main className="flex-1" data-id="element-1291">
        {/* Hero Section */}
        <div className="relative h-[40vh] min-h-[400px] w-full bg-slate-900" data-id="element-1292">
          <img src={procedure.heroImage} alt={`${procedure.name} medical facility`} className="absolute inset-0 w-full h-full object-cover opacity-40" data-id="element-1293" />
          <div className="absolute inset-0 flex items-center" data-id="element-1294">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full" data-id="element-1295">
              <Breadcrumb className="mb-6 text-slate-300" data-id="element-1296">
                <BreadcrumbList data-id="element-1297">
                  <BreadcrumbItem data-id="element-1298">
                    <BreadcrumbLink href="/" className="text-slate-300 hover:text-white" data-id="element-1299">
                      Home
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-slate-500" data-id="element-1300" />
                  <BreadcrumbItem data-id="element-1301">
                    <BreadcrumbLink href="/procedures" className="text-slate-300 hover:text-white" data-id="element-1302">
                      Procedures
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="text-slate-500" data-id="element-1303" />
                  <BreadcrumbItem data-id="element-1304">
                    <BreadcrumbPage className="text-white font-medium" data-id="element-1305">
                      {procedure.name}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4" data-id="element-1306">
                {procedure.name}
              </h1>
              <p className="text-xl text-slate-200 max-w-2xl" data-id="element-1307">
                {procedure.shortBlurb}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-1308">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-1309">
            <article className="prose prose-slate prose-lg max-w-none" data-id="element-1310">
              {procedure.content.split('\n\n').map((paragraph, idx) => <p key={idx} className="mb-6 text-slate-700 leading-relaxed" data-id="element-1311">
                  {paragraph.trim()}
                </p>)}
            </article>

            {/* Risks Section */}
            <div className="bg-red-50 border border-red-100 rounded-2xl p-8" data-id="element-1312">
              <div className="flex items-center gap-3 mb-4 text-red-800" data-id="element-1313">
                <ShieldAlert className="w-6 h-6" data-id="element-1314" />
                <h3 className="text-xl font-bold" data-id="element-1315">
                  Potential Risks & Complications
                </h3>
              </div>
              <p className="text-red-700 mb-4 text-sm" data-id="element-1316">
                All medical procedures carry inherent risks. While we partner
                with top-tier accredited facilities to minimize these, it is
                important to be aware of potential complications:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-red-800" data-id="element-1317">
                {procedure.risks.map(risk => <li key={risk} data-id="element-1318">{risk}</li>)}
              </ul>
            </div>

            {/* Price Comparison Table */}
            {procedure.priceComparison && <div className="mt-12" data-id="element-1319">
                <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-1320">
                  Illustrative Price Comparison
                </h3>
                <div className="overflow-x-auto rounded-xl border border-slate-200" data-id="element-1321">
                  <table className="w-full text-left text-sm text-slate-600" data-id="element-1322">
                    <thead className="bg-slate-50 text-slate-900 font-semibold border-b border-slate-200" data-id="element-1323">
                      <tr data-id="element-1324">
                        <th className="px-6 py-4" data-id="element-1325">Destination</th>
                        <th className="px-6 py-4" data-id="element-1326">Illustrative Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white" data-id="element-1327">
                      {procedure.priceComparison.map((item, idx) => <tr key={idx} className="hover:bg-slate-50" data-id="element-1328">
                          <td className="px-6 py-4 font-medium text-slate-900" data-id="element-1329">
                            {item.destination}
                          </td>
                          <td className="px-6 py-4" data-id="element-1330">{item.price}</td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500 mt-3" data-id="element-1331">
                  * Pricing is illustrative and varies by facility, patient
                  health status, and specific treatment plan.
                </p>
              </div>}
          </div>

          {/* Sidebar */}
          <div className="space-y-8" data-id="element-1332">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100" data-id="element-1333">
              <h3 className="text-xl font-bold text-slate-900 mb-6" data-id="element-1334">
                Procedure Overview
              </h3>

              <div className="space-y-6" data-id="element-1335">
                <div data-id="element-1336">
                  <div className="text-sm text-slate-500 mb-1 flex items-center gap-1" data-id="element-1337">
                    <Info className="w-4 h-4" data-id="element-1338" /> Illustrative Pricing
                  </div>
                  <div className="text-3xl font-bold text-slate-900" data-id="element-1339">
                    from {procedure.illustrativePriceFrom}
                  </div>
                </div>

                <div data-id="element-1340">
                  <div className="text-sm text-slate-500 mb-1 flex items-center gap-1" data-id="element-1341">
                    <Info className="w-4 h-4" data-id="element-1342" /> Illustrative Savings
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 text-base py-1" data-id="element-1343">
                    Up to {procedure.illustrativeSavingsPct} vs US
                  </Badge>
                </div>

                <div className="pt-4 border-t border-slate-200" data-id="element-1344">
                  <div className="flex items-start gap-3 mb-4" data-id="element-1345">
                    <Clock className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-1346" />
                    <div data-id="element-1347">
                      <div className="font-medium text-slate-900" data-id="element-1348">
                        Average Time
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-1349">
                        {procedure.averageProcedureTime}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 mb-4" data-id="element-1350">
                    <Calendar className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-1351" />
                    <div data-id="element-1352">
                      <div className="font-medium text-slate-900" data-id="element-1353">
                        Stay Required
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-1354">
                        {procedure.stayRequired}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-1355">
                    <FileText className="w-5 h-5 text-blue-600 mt-0.5" data-id="element-1356" />
                    <div data-id="element-1357">
                      <div className="font-medium text-slate-900" data-id="element-1358">
                        Insurance Details
                      </div>
                      <div className="text-sm text-slate-600 leading-relaxed" data-id="element-1359">
                        {procedure.insuranceDetails}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200" data-id="element-1360">
                  <div className="text-sm font-medium text-slate-900 mb-3" data-id="element-1361">
                    Top Destinations
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-1362">
                    {procedure.destinations.map(dest => <Badge key={dest} variant="outline" className="text-slate-600 bg-white" data-id="element-1363">
                        <MapPin className="w-3 h-3 mr-1" data-id="element-1364" />
                        {dest}
                      </Badge>)}
                  </div>
                </div>

                <Button className="w-full mt-4" size="lg" onClick={() => navigate('/contact')} data-id="element-1365">
                  Request a Free Quote
                </Button>
              </div>
            </div>

            <div className="bg-blue-50 rounded-2xl p-8 border border-blue-100" data-id="element-1366">
              <h3 className="text-lg font-bold text-blue-900 mb-2" data-id="element-1367">
                Why trust us?
              </h3>
              <p className="text-blue-800 text-sm leading-relaxed" data-id="element-1368">
                We exclusively coordinate care with facilities that maintain
                stringent international accreditations. Our network surgeons are
                highly credentialed, often holding board certifications from the
                US, UK, or Europe.
              </p>
            </div>
          </div>
        </div>
      </main>

      <AppFooter data-id="element-1369" />
    </div>;
}